import { translate } from '@jsverse/transloco';
import * as Highcharts from 'highcharts';
import moment from 'moment';
import { RGBAColor } from '@inmoment/the-kitchen/src/lib/Pickers/ColorPicker/ColorPicker.model';

import { FilterService } from 'app/modules/collection/services/filter.service';
import { ChartCustomColorService } from 'app/modules/dashboard/services/chart-custom-color.service';
import { ComparisonHandlerService } from 'app/modules/dashboard/services/comparison-handler-service';
import { WidgetChartOptions } from 'app/modules/widget/components/widget-chart/widget-chart.component';
import {
  Comment,
  CommentPoint,
  CommentTarget,
  Comparison,
  Dashboard,
  Widget,
} from 'app/providers/bridge-api/dashboard.service';
import { SentimentPolarityRange } from 'app/providers/storage-api/project.service';
import { Report, ReportService } from 'app/providers/storage-api/report.service';
import { TimezoneService } from 'app/services/timezone.service';
import { FilterUtils } from 'app/utils/filter.utils';
import { Utils } from 'app/utils/utils';

import { HighchartsOptions } from '../../chart-display.service';

export type CustomAnnotation = Highcharts.AnnotationsOptions & {
  custom?: {
    target: CommentTarget;
    dataSourceId: string;
  };
};

const DEFAULT_COMMENT_ANNOTATION_LABEL_HTML =
  '<span class="kxi-icon kxi-icon-comment_filled orca" style="font-size:24px"></span>';
// have to use inline styles because Highcharts can not use external CSS during image export
const cardStyle =
  'position:relative;z-index:6;font-family: IBM Plex Sans;font-size: 14px;font-weight: 400;line-height: 18.2px;text-align: left;text-underline-position: from-font;text-decoration-skip-ink: none;padding: 10px;border-radius: 8px;box-shadow: 2px 2px 9.5px 4px rgba(0, 0, 0, 0.25);background-color: #fff;color: #2d3a44;min-width: fit-content;max-width: 328px;width: max-content;';
const titleStyle =
  'font-weight: 700;flex-wrap: nowrap;overflow: hidden;text-overflow: ellipsis;white-space: nowrap;';
const contentStyle =
  'text-wrap:auto;white-space:break-spaces;min-height: 32px;max-height: 55px;align-content: center;overflow: hidden;text-overflow: ellipsis;display: -webkit-box;-webkit-line-clamp: 3;line-clamp: 3;-webkit-box-orient: vertical;';

export function commentToAnnotation(comment: Comment) {
  const annotation: CustomAnnotation = {
    id: comment.id,
    draggable: '',
    labelOptions: {
      useHTML: true,
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      distance: 1,
      shape: 'rect',
      style: {
        color: '#253746',
      },
    },
    labels: [
      {
        point: comment.target?.pointId ?? comment.target?.point,
        text: DEFAULT_COMMENT_ANNOTATION_LABEL_HTML,
      },
    ],
    visible: true,
    custom: {
      target: comment.target,
      dataSourceId: comment.payload.widgetDataSourceId,
    },
  };

  if (comment.titlePinned || comment.summaryPinned) {
    let annotationText = `<div class="comment-card-chart" style="${cardStyle}">`;
    if (comment.titlePinned) {
      annotationText += `<div style="${
        titleStyle + (comment.summaryPinned ? 'margin-bottom:10px;' : '')
      }">${comment.title}</div>`;
    }
    if (comment.summaryPinned) {
      annotationText += `<div style="${contentStyle}">${comment.summary}</div>`;
    }
    annotationText += '</div>';

    annotation.zIndex = 5;
    annotation.draggable = 'xy';
    annotation.labelOptions = {
      useHTML: true,
      allowOverlap: true,
      shadow: true,
      backgroundColor: 'white',
      borderColor: '#2d3a44',
      borderWidth: 1,
      borderRadius: 4,
      shape: 'connector',
    };
    annotation.labels = [
      {
        point: comment.target?.pointId ?? comment.target?.point,
        text: annotationText,
        ...(comment.payload.dragPosition && {
          x: comment.payload.dragPosition.x,
          y: comment.payload.dragPosition.y,
        }),
      },
    ];
  }

  return annotation;
}

export type CommentDataChangeReason =
  | 'UNKNOWN'
  | 'WEIGHTING_ADDED'
  | 'WEIGHTING_CHANGED'
  | 'WEIGHTING_REMOVED'
  | 'DOCUMENT_FILTER_CHANGED'
  | 'DATASOURCE_CHANGED'
  | 'WIDGET_GROUP_ADDED'
  | 'WIDGET_GROUP_CHANGED'
  | 'WIDGET_GROUP_REMOVED'
  | 'COMPARISON_ADDED'
  | 'COMPARISON_CHANGED'
  | 'COMPARISON_REMOVED'
  | 'RATING_FIELD_CHANGED'
  | 'RATING_SCALE_CHANGED'
  | 'SCORING_STRATEGY_CHANGED'
  | 'SCORING_SCALE_CHANGED'
  | 'BOX_SIZE_CHANGED'
  | 'BUCKET_DEFINITION_CHANGED'
  | 'BOX_DIRECTION_CHANGED'
  | 'METADATA_FIELD_CHANGED'
  | 'AGGERGATOR_CHANGED';

export interface CommentDataChange {
  y?: number;
  x?: number;
  reasons: CommentDataChangeReason[];
}

export abstract class AbstractChartReport {
  static DEFAULT_DATE_FORMAT = '%d-%b-%Y';
  static DEFAULT_DATE_TIME_FORMAT = '%d-%b-%Y %H:%M:%S';
  public readonly canExportAsPptx: boolean = true;

  public abstract readonly reportName: string;
  public abstract reportHandler: (
    dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    options: any
  ) => HighchartsOptions;
  public abstract getFields(): string[];

  public formatMetadataValueFactory = (value) => {
    return this.formatMetadataValue(this.metadataType, value);
  };

  formatMetadataValue = (metadataType, value) => {
    switch (metadataType) {
      case 'date':
        if (value === '&lt;blank&gt;') {
          return '&lt;blank&gt;';
        }
        return Highcharts.dateFormat(
          AbstractFeatureChartReport.DEFAULT_DATE_FORMAT,
          new Date(value).getTime()
        );
      case 'double':
        return Highcharts.numberFormat(value, 2);
      case 'keyword':
        return value || '&lt;blank&gt;';
      default:
        return value;
    }
  };

  lastDocumentCount: number;

  constructor(
    public options: WidgetChartOptions,
    protected reportService: ReportService,
    protected filterService: FilterService,
    protected comparisonService: ComparisonHandlerService,
    protected timezoneService: TimezoneService,
    public metadataType?: string
  ) {}

  public getParams() {
    const params: any = {
      timezone: this.timezoneService.timezone,
      analysisName: this.options.widgetDataSource
        ? this.options.widgetDataSource.analysisId
        : undefined,
      featureName: this.options.analyticItem,
      topN: this.options.limit === -1 ? '' : this.options.limit,
      excludedValues: this.options.excludedValues,
      entityTypeFilter: this.options.entityTypeFilter,
      selectedItems: this.options.selectedItems,
      dateInterval: this.options.dateInterval,
      sentimentScale: this.options.sentimentScale,
    };

    if (this.options.type === 'documentCount' && this.options.analyticItem === 'documents') {
      params.featureName = undefined;
    }

    if (this.options.aggregator) {
      //total_ is to deliminate the different aggregation inside of dashboard.
      //For elasticsearch the aggregation must continue to be AVG or SUM
      params.aggregate = this.options.aggregator.replace('total_', '');
    }

    if (!this.options.orderBySentiment) {
      params.orderBySentiment = this.options.orderBySentiment;
    }

    if (this.options.customFieldSelected) {
      params.customField = this.options.analyticItem;
    }

    if (this.options.standardFieldSelected) {
      params.standardField = this.options.analyticItem;
    }

    if (this.options.sectionPropertySelected) {
      // Extract the property name from sectionProperties.{propertyName}
      const propertyName = this.options.analyticItem.replace('sectionProperties.', '');
      params.featureName = `flatSections.${propertyName}`;
    }

    if (this.options.weightField) {
      params.weightField = this.options.weightField;
    }

    if (this.options.selectedSection) {
      params.featureSectionFilter = FilterUtils.convertSelectedSectionToFeatureSectionFilter(
        this.options.selectedSection
      );
    }

    // DASH-9694: Add comparison section filters as featureSectionFilters
    if (this.options.comparisonSectionFilters) {
      params.featureSectionFilter ??= [];
      params.featureSectionFilter.push(
        ...FilterUtils.convertSelectedSectionToFeatureSectionFilter(
          this.options.comparisonSectionFilters
        )
      );
    }

    if (this.options.widgetDataSource?.isSocial) {
      params.isSocial = this.options.widgetDataSource.isSocial;
    }

    return params;
  }

  public getMapData(_includeLabels = false): { [key: string]: string }[] {
    return null;
  }

  public getDefaultTitle(): string {
    return '';
  }

  public getOptionalFields(): string[] {
    return ['excludedValues', 'selectedItems'];
  }

  public getGraphTypes(): string[] {
    return [];
  }

  public getAggregators(): string[] {
    return [];
  }

  public hasTimeInterval(): boolean {
    return this.options.aggregator?.includes('time') || this.reportName.includes('OverTime');
  }

  public getChartSettings(): any[] {
    return [];
  }

  public isAnalyticItemRequired(): boolean {
    return false;
  }

  public isAnalyticItemSupported(_analyticItem: string): boolean {
    return true;
  }

  public getAnalyticItemNotSupportedTooltip(_analyticItem: string): string {
    return '';
  }

  public isSelectedItemRequired(): boolean {
    return false;
  }

  public canOnlyHaveOneSelectedItem(): boolean {
    return false;
  }

  public canHideItems(): boolean {
    return true;
  }

  public hasTaxonomyLevel(): boolean {
    return false;
  }

  public canDrilldownMentions(): boolean {
    return false;
  }

  public canDrilldownThemes(): boolean {
    return false;
  }

  public canDrilldown(): boolean {
    return true;
  }

  public canSort(): boolean {
    return true;
  }

  public canDisplayChartValues(): boolean {
    return false;
  }

  canColorByPoint(): boolean {
    return false;
  }

  isDisplayChartValuesDisabled(): boolean {
    return true;
  }

  isColorByPointDisabled(): boolean {
    return false;
  }

  displayChartValuesDisabledTooltip(): string {
    return undefined;
  }

  colorByPointDisabledTooltip(): string {
    return undefined;
  }

  public canSetChartMaximum(): boolean {
    return true;
  }

  public canSetChartMaximumHorizontal(): boolean {
    return false;
  }

  public canSetChartMaximumPercentage(): boolean {
    return false;
  }

  public canSetChartGoal(): boolean {
    return false;
  }

  public canSetDateMarkers(): boolean {
    return false;
  }

  public canShowAsStacked(): boolean {
    return false;
  }

  public getLimit() {
    return this.options.limit;
  }

  public setLimit(limit) {
    this.options.limit = limit;
  }

  public applyComparisonToSeries(comparison: Comparison, series, seriesIdx) {
    if (comparison?.filters[seriesIdx]) {
      series.custom ??= {};
      series.custom.seriesId = series.name;
      series.name = this.comparisonService.filterDisplayValueExtractorFn(comparison.type)(
        comparison.filters[seriesIdx]
      );
    }
  }

  chartItemToCommentPoint(item): CommentPoint {
    return {
      point: {
        x: item.x,
        y: item.y,
        xAxis: 0,
        yAxis: 0,
      },
      pointId: item.id,
      series: item.series?.custom?.seriesId ?? item.series?.name ?? item.category,
    };
  }

  getCommentsForSeries(
    series,
    widgetPayload: WidgetChartOptions,
    comparison?: Comparison
  ): Comment[] {
    if (!series.name) {
      return [...(widgetPayload.comments || [])];
    }

    let seriesId = series.custom?.seriesId ?? series.name;
    if (comparison?.filters.length > 1) {
      seriesId = series.name;
    }

    return (
      widgetPayload.comments?.filter(
        (c) =>
          c.target.series === seriesId || c.target.analyticItems.some((i) => i.name === seriesId)
      ) ?? []
    );
  }

  getCommentsForPoint(point, comments: Comment[]) {
    if (!comments) {
      return [];
    }

    const pointId = point.id ?? point.pointId ?? point.category;
    const isSingleSeries =
      point.series?.chart.series.filter((s) => !s.userOptions.custom?.seriesType).length === 1;

    return comments.filter(
      (c) =>
        c.target?.pointId === pointId && (isSingleSeries || c.target.series === point.series?.name)
    );
  }

  getCommentDataChange(
    comment: Comment,
    widget: Widget<WidgetChartOptions>,
    dashboard: Dashboard,
    series,
    dataProp = 'y'
  ): CommentDataChange {
    const currentData = this.getPointForComment(comment, series);
    //eslint-disable-next-line eqeqeq
    if (currentData && currentData[dataProp] != comment.target.point[dataProp]) {
      const changeReasons = [];
      //eslint-disable-next-line eqeqeq
      if (comment.payload.weightField != widget.payload.weightField) {
        if (!comment.payload.weightField) {
          changeReasons.push('WEIGHTING_ADDED');
        } else if (!widget.payload.weightField) {
          changeReasons.push('WEIGHTING_REMOVED');
        } else {
          changeReasons.push('WEIGHTING_CHANGED');
        }
      }

      //eslint-disable-next-line eqeqeq
      if (comment.payload.comparison != widget.payload.comparison) {
        if (!comment.payload.comparison) {
          changeReasons.push('COMPARISON_ADDED');
        } else if (!widget.payload.comparison) {
          changeReasons.push('COMPARISON_REMOVED');
        } else {
          changeReasons.push('COMPARISON_CHANGED');
        }
      }

      const currentWidgetGroup = Object.keys(dashboard.widgetGroups).find((id) =>
        dashboard.widgetGroups[id].widgets.includes(comment.payload.widgetId)
      );
      //eslint-disable-next-line eqeqeq
      if (comment.payload.widgetGroup != currentWidgetGroup) {
        if (!comment.payload.widgetGroup) {
          changeReasons.push('WIDGET_GROUP_ADDED');
        } else if (!currentWidgetGroup) {
          changeReasons.push('WIDGET_GROUP_REMOVED');
        } else {
          changeReasons.push('WIDGET_GROUP_CHANGED');
        }
      }

      const safeCommentFilters = comment.payload.filters ?? FilterService.getDefaultFilterSet();
      const safeWidgetFilters = widget.payload.filters ?? FilterService.getDefaultFilterSet();
      const safeCommentUserSearch = comment.payload.userSearch ?? {
        userSearch: { and: [], or: [], not: [] },
      };
      const safeWidgetUserSearch = widget.payload.userSearch ?? {
        userSearch: { and: [], or: [], not: [] },
      };

      if (
        !Utils.deepEquals(safeCommentFilters, safeWidgetFilters) ||
        !Utils.deepEquals(safeCommentUserSearch, safeWidgetUserSearch)
      ) {
        changeReasons.push('DOCUMENT_FILTER_CHANGED');
      }

      if (comment.payload.aggregator !== widget.payload.aggregator) {
        changeReasons.push('AGGREGATOR_CHANGED');
      }

      const reportSpecificChange = this.getReportSpecificCommentDataChange(
        comment,
        widget,
        dashboard,
        currentData,
        dataProp
      );
      if (reportSpecificChange) {
        changeReasons.push(...reportSpecificChange.reasons);
      }

      if (!changeReasons.length) {
        changeReasons.push('UNKNOWN');
      }

      return {
        [dataProp]: currentData?.[dataProp],
        reasons: changeReasons,
      };
    }
  }

  protected getReportSpecificCommentDataChange(
    _comment: Comment,
    _widget: Widget<WidgetChartOptions>,
    _dashboard: Dashboard,
    _currentData,
    _dataProp: string
  ): CommentDataChange {
    return;
  }

  protected getPointForComment(comment: Comment, series) {
    return series.data?.find((d) => comment.target?.pointId === (d.id ?? d.pointId ?? d.category));
  }

  isCommentVisible(comment: Comment, widget: Widget, series) {
    if (widget.payload.commentsHidden) {
      return false;
    }

    return this.isCommentOnScreen(comment, widget, series);
  }

  isCommentOnScreen(comment: Comment, widget: Widget, series) {
    if (comment.payload.widgetDataSourceId !== widget.dataSourceId) {
      return false;
    }

    return !!this.getPointForComment(comment, series);
  }

  public getHighchartsOptions(
    dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    options: HighchartsOptions
  ): HighchartsOptions {
    options.tooltip.outside = true;
    options.tooltip.valueDecimals = 1;
    options.tooltip.xDateFormat = AbstractChartReport.DEFAULT_DATE_TIME_FORMAT;

    if (options.tooltip.headerFormat === undefined) {
      const dateTextPart =
        widget.payload.dateInterval === 'week'
          ? 'widgets.charting.tooltip.date-part-week-bold'
          : 'widgets.charting.tooltip.date-part';
      options.tooltip.headerFormat = `${translate(dateTextPart, {
        date: "'{point.key:" + AbstractChartReport.DEFAULT_DATE_TIME_FORMAT + "'}",
      })}<br/>`;
    }

    if (!options.tooltip.pointFormatter) {
      options.tooltip.pointFormatter = function () {
        return `${translate('widgets.charting.tooltip.name-part', {
          name: this.series.name,
        })} <span style="color: ${this.color}">\u25CF</span> ${Utils.addThousandSeparator(
          Utils.round(this.y, this.series.options.maxDecimals)
        )}`;
      };
    }

    if (
      this.canDisplayChartValues() &&
      !this.isDisplayChartValuesDisabled() &&
      widget.payload.displayChartValues
    ) {
      options.plotOptions = options.plotOptions || { series: {} };
      options.plotOptions.series = options.plotOptions.series || { dataLabels: {} };
      options.plotOptions.series.dataLabels = {
        ...(options.plotOptions.series.dataLabels || {}),
        enabled: true,
        inside: false,
        formatter() {
          return `${Utils.round((this as Highcharts.Point).y, 2)}`;
        },
      };

      if (widget.payload.aggregator?.includes('percentage')) {
        options.plotOptions.series.dataLabels.format = '{point.y:.1f}';
      }
    }

    if (this.canColorByPoint() && !this.isColorByPointDisabled() && widget.payload.colorByPoint) {
      options.plotOptions = options.plotOptions || { series: {} };
      options.plotOptions.series = options.plotOptions.series || {};
      options.plotOptions.series.colorByPoint = true;
    }

    let yAxisForSetChartAxisValues = options.yAxis;
    if (Array.isArray(options.yAxis) && options.yAxis.length > 1) {
      yAxisForSetChartAxisValues = options.yAxis[1];
    }

    this.setChartAxisValues(
      widget.payload.chartMinimum,
      widget.payload.chartMaximum,
      widget.payload.chartInterval,
      yAxisForSetChartAxisValues
    );

    if (widget.payload.chartMaximumHorizontal) {
      this.setChartAxisValues(
        undefined,
        widget.payload.chartMaximumHorizontal,
        undefined,
        options.xAxis
      );
    }

    if (
      this.canSetChartGoal() &&
      typeof widget.payload.chartGoal === 'number' &&
      this.options.graphType !== 'pie' &&
      this.options.graphType !== 'gauge' &&
      options.series?.length &&
      options.series[0].data?.length
    ) {
      const plotLines = [
        {
          color: widget.payload.chartGoalColor,
          width: 2,
          value: widget.payload.chartGoal,
          label: {
            text: widget.payload.chartGoalDisplayAs,
            align: 'right',
            x: -5,
            rotation: 0,
          },
          zIndex: 5,
        },
      ];
      let yAxis = options.yAxis;
      if (Array.isArray(options.yAxis) && options.yAxis.length) {
        yAxis = options.yAxis[0];
      }
      Object.assign(yAxis, {
        plotLines,
      });

      options.series.push({
        showInLegend: false,
        type: 'scatter',
        marker: {
          enabled: false,
        },
        enableMouseTracking: false,
        data: [
          {
            x: options.series[0].data[0].x,
            y: widget.payload.chartGoal,
          },
        ],
        visible: false,
        custom: {
          seriesType: 'chartGoal',
        },
      });
    }

    if (
      typeof widget.payload.chartGoal === 'number' &&
      widget.payload.chartGoalShade !== undefined &&
      widget.payload.chartGoalShade !== 'none' &&
      this.options.graphType !== 'pie' &&
      this.options.graphType !== 'gauge'
    ) {
      const color: RGBAColor = ChartCustomColorService.colorStringToRgbaColor(
        widget.payload.chartGoalColor
      );
      const plotBands = [
        {
          color: `rgba(${color.red},${color.green},${color.blue}, 0.05)`,
          from: widget.payload.chartGoal,
          to:
            widget.payload.chartGoalShade === 'above'
              ? Number.POSITIVE_INFINITY
              : Number.NEGATIVE_INFINITY,
        },
      ];
      let yAxis = options.yAxis;
      if (Array.isArray(options.yAxis) && options.yAxis.length) {
        yAxis = options.yAxis[0];
      }
      Object.assign(yAxis, {
        plotBands,
      });
    }

    if (widget.payload.dateMarkers?.length) {
      const plotLines = widget.payload.dateMarkers.map((dm) => ({
        width: 2,
        dashStyle: 'longdashdot',
        zIndex: 5,
        color: `rgba(${dm.color.red}, ${dm.color.green}, ${dm.color.blue}, 1)`,
        value: moment(dm.date).tz(this.timezoneService.timezone, true).valueOf(),
        label: {
          text: dm.displayName,
          verticalAlign: 'middle',
          textAlign: 'center',
        },
      }));

      Object.assign(options.xAxis, { plotLines });
    }

    if (response.comparisonBuckets?.length) {
      const comparison = dashboard.comparisons[widget.payload.comparison];

      options.series.forEach((serie, i) => this.applyComparisonToSeries(comparison, serie, i));
    }

    if (widget.payload.comments?.length) {
      options.annotations = this.createChartAnnotations(options.series, widget, dashboard);
    }

    return options;
  }

  createChartAnnotations(
    highchartsSeries,
    widget: Widget<WidgetChartOptions>,
    dashboard: Dashboard
  ) {
    let annotations = [];

    const createAnnotation = (comment, series) => {
      const annotation = commentToAnnotation(comment);
      annotation.visible = this.isCommentOnScreen(comment, widget, series);

      const dataChange = this.getCommentDataChange(comment, widget, dashboard, series);
      if (dataChange) {
        series.custom ??= {};
        series.custom.annotationChanges ??= <Record<string, CommentDataChange>>{};
        series.custom.annotationChanges[comment.id] = dataChange;
      }

      return annotation;
    };

    const realSeries = highchartsSeries.filter((s) => !s.custom?.seriesType);

    for (const series of realSeries) {
      let comments = widget.payload.comments ?? [];
      if (realSeries.length > 1) {
        comments = this.getCommentsForSeries(
          series,
          widget.payload,
          dashboard.comparisons[widget.payload.comparison]
        );
      }

      for (const comment of comments) {
        const annotation = createAnnotation(comment, series);

        if (annotation.visible) {
          annotation.visible =
            !!annotation.draggable ||
            !annotations.some((a) => a.labels[0].point === annotation.labels[0].point);

          if (annotation.draggable) {
            annotations
              .filter(
                (ann) =>
                  ann.id !== annotation.id && ann.labels[0].point === annotation.labels[0].point
              )
              .forEach((oa) => (oa.visible = false));
          }
        }

        annotations.push(annotation);
      }
    }

    annotations = annotations.filter((a) => a.visible);

    if (widget.payload.commentsHidden) {
      annotations.forEach((a) => (a.visible = false));
    }

    return annotations;
  }

  protected setChartAxisValues(min: number, max: number, tickInterval: number, axis: any): void {
    if (!axis) {
      return;
    }

    delete axis.startOnTick;
    delete axis.endOnTick;

    if (min != null) {
      axis.min = min;
      axis.startOnTick = false;
    }

    if (max != null) {
      axis.max = max;
      axis.endOnTick = false;
    }

    if (tickInterval != null) {
      axis.tickInterval = tickInterval;
    }
  }

  public async processResponse(report, _filterQuery?: string): Promise<any> {
    return report;
  }

  public async executeReport(
    format: 'json' | 'csv',
    passedQuery: string
  ): Promise<Report | string> {
    const params = await this.getParams();

    const response = await this.reportService.getReport(
      this.options.widgetDataSource.collectionName,
      this.options.widgetDataSource.groupId,
      this.reportName,
      params,
      format,
      passedQuery,
      this.filterService.buildFilterStringClearPolarityFilters(
        this.options.filters,
        this.options.analyticItem
      ),
      undefined,
      this.options.projectId
    );

    //Lesser of two evils for now to pass the filterQuery string in to processResponse for the wordcloud which
    //calls execute report again for the sentiment.  Getting the relevant information into the worldcloud chart when
    //it's making it's call would be a large overhaul.
    if (format === 'json') {
      this.lastDocumentCount = (<Report>response).totalHits;

      return await this.processResponse(response, passedQuery);
    }

    return response;
  }

  public async executeReportForMentions(
    label: string,
    format: 'json' | 'csv',
    passedQuery?: string
  ): Promise<Report | string> {
    const filterQuery = passedQuery || this.filterService.buildQueryString(this.options.filters);
    const params = await this.getParams();
    const response = await this.reportService.getReport(
      this.options.widgetDataSource.collectionName,
      this.options.widgetDataSource.groupId,
      'featureTopMentions',
      Object.assign({ label }, params),
      format,
      filterQuery,
      this.filterService.buildFilterStringClearPolarityFilters(
        this.options.filters,
        this.options.analyticItem
      ),
      undefined,
      this.options.projectId
    );

    if (format === 'json') {
      this.lastDocumentCount = (<Report>response).totalHits;

      return await this.processResponse(response);
    }

    return response;
  }

  public async executeReportForFeaturePhrases(
    label: string,
    featureType: string,
    format: 'json' | 'csv',
    passedQuery?: string,
    taxonomyNode?: string
  ): Promise<Report | string> {
    const filterQuery = passedQuery || this.filterService.buildQueryString(this.options.filters);
    if (taxonomyNode) {
      label = 'taxonomy_phrases';
    }
    const params = await this.getParams();
    params['featureType'] = featureType;
    params['taxonomyNode'] = taxonomyNode || '';
    const response = await this.reportService.getReport(
      this.options.widgetDataSource.collectionName,
      this.options.widgetDataSource.groupId,
      'featurePhrases',
      Object.assign({ label }, params),
      format,
      filterQuery,
      this.filterService.buildFilterStringClearPolarityFilters(
        this.options.filters,
        this.options.analyticItem
      ),
      undefined,
      this.options.projectId
    );

    if (format === 'json') {
      this.lastDocumentCount = (<Report>response).totalHits;

      return await this.processResponse(response);
    }

    return response;
  }

  public async executeReportForTaxonomyMentions(
    taxonomyNode: string,
    format: 'json' | 'csv',
    passedQuery?: string
  ): Promise<Report | string> {
    const label = 'taxonomy_mentions';
    const filterQuery = passedQuery || this.filterService.buildQueryString(this.options.filters);
    const params = await this.getParams();
    params['taxonomyNode'] = taxonomyNode;
    const response = await this.reportService.getReport(
      this.options.widgetDataSource.collectionName,
      this.options.widgetDataSource.groupId,
      'featureTopMentions',
      Object.assign({ label }, params),
      format,
      filterQuery,
      this.filterService.buildFilterStringClearPolarityFilters(
        this.options.filters,
        this.options.analyticItem
      ),
      undefined,
      this.options.projectId
    );

    if (format === 'json') {
      this.lastDocumentCount = (<Report>response).totalHits;

      return await this.processResponse(response);
    }

    return response;
  }

  public async executeReportForThemes(
    label: string,
    format: 'json' | 'csv',
    passedQuery?: string,
    taxonomyNode?: string
  ): Promise<Report | string> {
    const filterQuery = passedQuery || this.filterService.buildQueryString(this.options.filters);
    if (taxonomyNode) {
      label = 'taxonomy_themes';
    }
    const params = await this.getParams();
    params['taxonomyNode'] = taxonomyNode || '';
    const response = await this.reportService.getReport(
      this.options.widgetDataSource.collectionName,
      this.options.widgetDataSource.groupId,
      'featureTopThemes',
      Object.assign({ label }, params),
      format,
      filterQuery,
      this.filterService.buildFilterStringClearPolarityFilters(
        this.options.filters,
        this.options.analyticItem
      ),
      undefined,
      this.options.projectId
    );

    if (format === 'json') {
      this.lastDocumentCount = (<Report>response).totalHits;

      return await this.processResponse(response);
    }

    return response;
  }

  public async executeReportForTaxonomy(
    label: string,
    format: 'json' | 'csv',
    passedQuery?: string
  ): Promise<Report | string> {
    const filterQuery = passedQuery || this.filterService.buildQueryString(this.options.filters);
    const params = await this.getParams();
    params.parentId = label;
    const response = await this.reportService.getReport(
      this.options.widgetDataSource.collectionName,
      this.options.widgetDataSource.groupId,
      'taxonomyLevel',
      Object.assign({ label }, params),
      format,
      filterQuery,
      this.filterService.buildFilterStringClearPolarityFilters(
        this.options.filters,
        this.options.analyticItem
      ),
      undefined,
      this.options.projectId
    );

    if (format === 'json') {
      this.lastDocumentCount = (<Report>response).totalHits;

      return await this.processResponse(response);
    }

    return response;
  }

  public async executeReportForVolumeCPPP(
    format: 'json' | 'csv',
    passedQuery: string,
    selectedItems
  ): Promise<Report | string> {
    const params = await this.getParams();
    params.selectedItems = selectedItems;

    const response = await this.reportService.getReport(
      this.options.widgetDataSource.collectionName,
      this.options.widgetDataSource.groupId,
      this.reportName,
      params,
      format,
      passedQuery,
      this.filterService.buildFilterStringClearPolarityFilters(
        this.options.filters,
        this.options.analyticItem
      ),
      undefined,
      this.options.projectId
    );

    //Lesser of two evils for now to pass the filterQuery string in to processResponse for the wordcloud which
    //calls execute report again for the sentiment.  Getting the relevant information into the worldcloud chart when
    //it's making it's call would be a large overhaul.
    if (format === 'json') {
      this.lastDocumentCount = (<Report>response).totalHits;

      return await this.processResponse(response, passedQuery);
    }

    return response;
  }

  public configureChart(
    dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    options: HighchartsOptions = {}
  ): HighchartsOptions {
    options = this.reportHandler(dashboard, widget, response, options);
    options = this.getHighchartsOptions(dashboard, widget, response, options);
    return options;
  }

  public getSentimentCountsForTooltips(response, sentimentScale) {
    const sumTotal = (obj) => Object.values(obj).reduce((a: number, b: number) => a + b, 0);

    const data = [];
    const tooltipTotals = [];

    for (const key of Object.getOwnPropertyNames(response.items.count)) {
      const value = response.items.count[key];

      data.push([key, value]);
    }

    switch (sentimentScale) {
      default:
      case '':
      case '3':
        for (const value of data) {
          tooltipTotals.push({
            pointId: value[0],
            total: sumTotal(value[1]),
            positive: value[1]['positive'],
            negative: value[1]['negative'],
            neutral: value[1]['neutral'],
          });
        }
        break;
      case '5':
        for (const value of data) {
          tooltipTotals.push({
            pointId: value[0],
            total: sumTotal(value[1]),
            ['very positive']: value[1]['very positive'],
            ['somewhat positive']: value[1]['somewhat positive'],
            neutral: value[1]['neutral'],
            ['somewhat negative']: value[1]['somewhat negative'],
            ['very negative']: value[1]['very negative'],
          });
        }
        break;
    }
    return tooltipTotals;
  }

  formatSentimentTooltips(itemValue: number, tooltipTotals, series, point, percentage, widget, id) {
    const showAsStacked = widget.payload.showAsStacked;
    const showByPercentage = widget.payload.aggregator === 'percentage';
    const index = series.data.indexOf(point);
    const total = series.chart.series
      .filter((s) => s.name !== 'Total volume')
      .reduce((prev, curr) => prev + curr.data[index].y, 0);
    const itemTotals = tooltipTotals.find((item) => item.pointId === id);
    let percent: number;
    const key = series.name === 'Total volume' ? 'total' : series.name;
    if (showAsStacked) {
      percent = total ? Math.round((itemValue * 100) / total) : percentage;
    } else {
      percent = Utils.roundNearest((itemTotals[key.toLowerCase()] / itemTotals.total) * 100);
    }
    const count =
      showByPercentage && !showAsStacked
        ? Utils.addThousandSeparator(itemTotals[key.toLowerCase()])
        : Utils.addThousandSeparator(itemValue);

    return { count, percent };
  }

  static getSeriesSymbolChar(symbol) {
    switch (symbol) {
      case 'circle':
        return '●';
      case 'diamond':
        return '♦';
      case 'square':
        return '■';
      case 'triangle':
        return '▲';
      case 'triangle-down':
        return '▼';
      default:
        return '●';
    }
  }
}

export abstract class AbstractDocumentsChartReport extends AbstractChartReport {
  getFields() {
    return ['analyticItem', 'aggregator', 'graphType'];
  }

  getAggregators() {
    return ['total', 'time'];
  }

  public canSort(): boolean {
    return false;
  }

  getHighchartsOptions(dashboard, widget, response, options) {
    const ret = super.getHighchartsOptions(dashboard, widget, response, options);
    if (widget.payload.type === 'documentCount') {
      const documentCount = this.options.filteredDocumentCount;

      ret.tooltip = {
        headerFormat: `${translate('widgets.charting.tooltip.name-part', {
          name: "'{point.key'}",
        })}<br/>`,
        pointFormatter() {
          const percent = Math.round((this.y * 100) / documentCount);
          let countLabel = `${translate('widgets.charting.tooltip.count-total-percentage-part', {
            count: Utils.addThousandSeparator(this.y),
            total: Utils.addThousandSeparator(documentCount),
            percentage: percent.toFixed(0),
          })}`;
          if (widget.payload.analyticItem === 'documents') {
            countLabel = '';
          }
          return `
            <span style="color: ${this.color}">\u25CF</span> <b>${this.series.name}</b> <br/>
            ${countLabel}
          `;
        },
      };
    }
    return ret;
  }
}

export abstract class AbstractFeatureChartReport extends AbstractChartReport {
  static getSentimentColor(sentimentRanges: SentimentPolarityRange[], sentimentScore: number) {
    let color = '#7CB5EC';
    sentimentScore =
      (sentimentScore || sentimentScore === 0) &&
      !isNaN(sentimentScore) &&
      String(sentimentScore) !== 'Infinity'
        ? Number(sentimentScore.toFixed(2))
        : undefined;

    switch (sentimentRanges.length) {
      default:
      case 3:
        if (sentimentScore !== undefined) {
          if (sentimentScore <= sentimentRanges[0].max) {
            color = '#DF5353';
          } else if (
            sentimentScore > sentimentRanges[1].min &&
            sentimentScore < sentimentRanges[1].max
          ) {
            color = '#929292';
          } else {
            color = '#55BF3B';
          }
        }
        break;

      case 5:
        if (sentimentScore !== undefined) {
          if (sentimentScore <= sentimentRanges[0].max) {
            color = '#DF5353';
          } else if (
            sentimentScore > sentimentRanges[1].min &&
            sentimentScore <= sentimentRanges[1].max
          ) {
            color = '#FE9999';
          } else if (
            sentimentScore > sentimentRanges[2].min &&
            sentimentScore < sentimentRanges[2].max
          ) {
            color = '#cccccc';
          } else if (
            sentimentScore >= sentimentRanges[3].min &&
            sentimentScore < sentimentRanges[3].max
          ) {
            color = '#BFFFB2';
          } else {
            color = '#55BF3B';
          }
        }
        break;
    }
    return color;
  }

  static getSentimentValueColor(sentimentScale: string, sentimentValue: string) {
    let color = '#cccccc';

    switch (sentimentScale) {
      default:
      case '':
      case '3':
        if (sentimentValue === 'negative') {
          color = '#FF0000';
        } else if (sentimentValue === 'positive') {
          color = '#009E0F';
        } else if (sentimentValue === 'neutral') {
          color = '#cccccc';
        } else {
          color = '#7CB5EC';
        }
        break;

      case '5':
        if (sentimentValue === 'very positive') {
          color = '#009E0F';
        } else if (sentimentValue === 'somewhat positive') {
          color = '#6B8C5B';
        } else if (sentimentValue === 'neutral') {
          color = '#cccccc';
        } else if (sentimentValue === 'somewhat negative') {
          color = '#B16B6B';
        } else if (sentimentValue === 'very negative') {
          color = '#FF0000';
        } else {
          color = '#7CB5EC';
        }
        break;
    }
    return color;
  }

  getFields() {
    const fields = ['analyticItem', 'limit', 'aggregator', 'graphType'];

    if (this.isSelectedItemRequired()) {
      fields.push('selectedItems');
    } else {
      fields.push('excludedValues');
    }

    return fields;
  }

  getAggregators() {
    return ['total', 'time'];
  }

  isSelectedItemRequired() {
    return this.options.limit === -1;
  }

  getTaxonomyHighChartsOptions(dashboard, widget, response, options): HighchartsOptions {
    const ret = super.getHighchartsOptions(dashboard, widget, response, options);

    const setItemDisplayName = (seriesData) => {
      for (const item of seriesData) {
        if (item.pointId) {
          const taxonomyNode = ret.taxonomyNodes[item.pointId];
          if (taxonomyNode) {
            item['pointDisplay'] = taxonomyNode.name;
          }
        }
      }
    };

    ret.taxonomyNodes =
      response.currentPeriodData?.extra.taxonomyNodes ?? response.extra.taxonomyNodes;

    response.comparisonBuckets?.forEach((bucket) => {
      Object.entries(bucket.extra.taxonomyNodes).forEach(([k, v]) => (ret.taxonomyNodes[k] ??= v));
    });

    ret.series.forEach((serie) => setItemDisplayName(serie.data));

    const taxonomyNodeFromIdFn = this.getTaxonomyNodeFromIdFn(ret);

    if (this.options.graphType === 'pie' || this.options.graphType === 'gauge') {
      ret.series[0].dataLabels = ret.series[0].dataLabels || {};
      ret.series[0].dataLabels.formatter = function () {
        const labelId = this.value?.split(':')[0] || this.value || this.key;
        const taxonomyNode = taxonomyNodeFromIdFn(labelId);
        return taxonomyNode?.name || this.key;
      };
    }
    return ret;
  }

  getTaxonomyNodeFromIdFn(highchartsOptions) {
    const nodes = Object.assign({}, highchartsOptions.taxonomyNodes);
    return (id) => {
      const taxonomyNode = nodes[id];
      const name = taxonomyNode?.name || id;
      const path = taxonomyNode?.path || '';

      return { name, path };
    };
  }
}
